/**
 * Per-post share images: each entry is the post's FIRST on-page image, used for
 * og:image / twitter:image and the BlogPosting JSON-LD image. Posts without an
 * entry fall back to the sitewide og-image.jpg.
 */
import badgeSwap from "@/assets/google-guaranteed-to-google-verified-badge-v2.png.asset.json";
import lsaStack from "@/assets/local-services-ads-results-page-position-v2.png.asset.json";
import leadLife from "@/assets/local-services-ads-lead-lifecycle-v2.png.asset.json";
import cliffVsSlope from "@/assets/google-ads-conversions-stopped-cliff-vs-slope-v2.png.asset.json";
import metaCheckouts from "@/assets/meta-purchases-vs-checkouts-check-v2.png.asset.json";
import callTrackingDni from "@/assets/call-tracking-dynamic-number-insertion-v2.png.asset.json";
import dtcBudgetSplit from "@/assets/dtc-meta-google-budget-split-70-30-v2.png.asset.json";
import offlineImportFlow from "@/assets/google-ads-offline-conversion-import-flow-v2.png.asset.json";
import lsaBidGrid from "@/assets/lsa-bids-by-job-type-and-zip-v2.png.asset.json";
import poolFunnels from "@/assets/pool-service-meta-ads-two-funnels-v2.png.asset.json";

export interface BlogShareImage {
  url: string;
  alt: string;
  /** Display size; defaults to 1600x900 when omitted. */
  width?: number;
  height?: number;
}

export const BLOG_SHARE_IMAGES: Record<string, BlogShareImage> = {
  "google-guaranteed-badge-google-verified": {
    url: badgeSwap.url,
    width: 1440,
    height: 666,
    alt: "Local Services Ads listing showing the old Google Guaranteed badge struck through and the new Google Verified badge",
  },
  "local-services-ads-for-contractors": {
    url: lsaStack.url,
    width: 1440,
    height: 788,
    alt: "Simplified Google results page showing Local Services Ads above search ads, map results and organic results",
  },
  "mastering-lsa-disputes-recover-20-of-monthly-ad-spend": {
    url: leadLife.url,
    width: 1440,
    height: 595,
    alt: "Five stages of a Local Services lead from first contact to credited or not",
  },
  "why-did-my-google-ads-conversions-stop": {
    url: cliffVsSlope.url,
    width: 1440,
    height: 719,
    alt: "Two line charts of daily conversions: a sudden drop on one date points to a tracking problem, a slow decline over weeks points to the market",
  },
  "why-does-meta-report-more-purchases-than-my-store": {
    url: metaCheckouts.url,
    width: 1440,
    height: 660,
    alt: "Two Meta Ads Manager funnels: in a working account purchases are smaller than checkouts initiated; in a broken one purchases are larger",
  },
  "call-tracking-for-contractors": {
    url: callTrackingDni.url,
    width: 1440,
    height: 647,
    alt: "Five steps of dynamic number insertion: a visitor arrives, the phone number swaps, they call, the call is logged, and it is sent to the ad platform as a conversion",
  },
  "meta-vs-google-ads-dtc-budget-allocation-under-50k": {
    url: dtcBudgetSplit.url,
    width: 1440,
    height: 702,
    alt: "A starting budget split for DTC brands under $50K a month: 70% Meta for demand generation, 30% Google for capturing high-intent searches",
  },
  "google-ads-offline-conversion-imports-vs-form-fill-bidding": {
    url: offlineImportFlow.url,
    width: 1440,
    height: 729,
    alt: "Offline conversion import flow: the ad click adds a GCLID, the lead form stores it, the CRM keeps it, and qualified, quoted and closed stages are uploaded back to Google Ads",
  },
  "how-should-you-set-lsa-bids-by-service-type-and-zip-code": {
    url: lsaBidGrid.url,
    width: 1440,
    height: 687,
    alt: "Local Services Ads bid grid by job type and ZIP code: bid highest for high-value jobs in core ZIPs, lower or pause low-margin jobs in distant ZIPs",
  },
  "meta-ad-strategies-for-pool-cleaning-vs-repair-leads": {
    url: poolFunnels.url,
    width: 1440,
    height: 681,
    alt: "Two Meta ad funnels for pool service: a peace-of-mind funnel for weekly cleaning subscriptions and a problem-solution funnel for high-ticket repairs",
  },
};
